/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { ClientEvent, ClientEvents, parseMessage, ServerEvent, ServerEvents } from 'shared';

import { jwtToken } from '../utils/storageHelpers';
import { wsServer } from './wsServer';

type ServerEventObserver<T extends ServerEvents = any> = (event: ServerEvent<T>) => any;

const socketClient = (() => {
  let ws: WebSocket | undefined;
  let client: Promise<WebSocket> | undefined;
  let observers: ServerEventObserver[] = [];
  const observersByType: Partial<{ [T in ServerEvents]: ServerEventObserver[] }> = {};
  let reconnectAttempts: number = 0;

  const connect = (force = false): Promise<WebSocket> => {
    if (client && !force) {
      return client;
    }
    disconnect();
    return (client = new Promise(resolve => {
      const socket = new WebSocket(wsServer);

      socket.onopen = () => {
        reconnectAttempts = 0;
        const authEvent: ClientEvent<ClientEvents.Authentication> = {
          type: ClientEvents.Authentication,
          payload: { authenticationToken: jwtToken.get() },
        };
        socket.send(JSON.stringify(authEvent));
        ws = socket;
        resolve(socket);
      };
      socket.onerror = () => {
        console.error('could not connect to socket server');
        // reject("unexpected socket error");
      };

      socket.onclose = () => {
        if (reconnectAttempts >= 5) {
          disconnect();
          return;
        }
        reconnectAttempts++;
        console.error('Socket connection closed. Trying to reconnect in 3 seconds...');
        setTimeout(() => {
          void connect(true);
        }, 3000);
      };

      socket.onmessage = async message => {
        const event = (await parseMessage(message.data)) as ServerEvent<ServerEvents>;
        const type = event.type;
        broadcast(event);
        broadcastByType(type, event);
      };
    }));
  };

  function subscribe<T extends ServerEvents>(observer: ServerEventObserver<T>, type: T): any;
  function subscribe<T extends ServerEvents>(observer: ServerEventObserver<T>): any;
  function subscribe<T extends ServerEvents>(observer: ServerEventObserver<T>, type?: T): any {
    if (type) {
      observersByType[type] = observersByType[type] || [];
      (observersByType[type] as ServerEventObserver[]).push(observer);
    } else {
      observers.push(observer as ServerEventObserver);
    }
  }

  function unsubscribe(observer: ServerEventObserver, type: ServerEvents): any;
  function unsubscribe(observer: ServerEventObserver): any;
  function unsubscribe(observer: ServerEventObserver, type?: ServerEvents) {
    if (type) {
      const obs = observersByType[type] || [];
      obs.filter(fn => fn !== observer);
      observersByType[type] = obs;
    } else {
      observers = observers.filter(fn => fn !== observer);
    }
  }

  const broadcast = (event: ServerEvent<ServerEvents>) => {
    observers.forEach((observer: ServerEventObserver) => observer(event));
  };

  const broadcastByType = <T extends ServerEvents>(type: T, event: ServerEvent<T>) => {
    const obs = (observersByType[type] as ServerEventObserver[]) || [];
    obs.forEach((observer: ServerEventObserver) => observer(event));
  };

  const disconnect = () => {
    if (client) {
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      ws = undefined;
      client = undefined;
    }
  };

  const getWs = () => ws;

  const emit = async (data: ServerEvent<ServerEvents>) => {
    const connection = await connect();
    connection.send(JSON.stringify(data));
  };

  const isConnected = () => !!ws && ws.readyState === WebSocket.OPEN;

  return {
    connect,
    disconnect,
    getWs,
    subscribe,
    unsubscribe,
    emit,
    isConnected,
  };
})();

export { socketClient };
